import { useCallback, useEffect, useState } from "react";
import { api, errMsg, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Buildings, CheckCircle, Star, Trash, UploadSimple } from "@phosphor-icons/react";

const FIELDS = [["name", "Nome breve (usato nei report)"], ["legal_name", "Ragione sociale"],
  ["vat", "Partita IVA"], ["tax_code", "Codice fiscale"], ["address", "Indirizzo"],
  ["zip_code", "CAP"], ["city", "Città"], ["province", "Provincia"], ["phone", "Telefono"],
  ["email", "Email"]];

export default function Companies() {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/companies")).data);
      setCurrent((await api.get("/companies/current")).data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      setForm(null);
      window.dispatchEvent(new Event("company-updated"));
      if (refresh) await refresh();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadLogo = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await act(() => api.post(`/companies/${id}/logo`, fd), "Logo aggiornato");
  };

  return (
    <div className="space-y-4" data-testid="companies-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Aziende</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ogni azienda ha dipendenti, presenze, sicurezza e pesi separati. L'intestazione dell'azienda attiva
            compare su tutti i report.
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" className="rounded-sm font-semibold" data-testid="company-add"
            onClick={() => setForm({ name: "" })}>
            <Plus size={15} className="mr-1.5" /> Nuova azienda
          </Button>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex items-center gap-3" data-testid="company-active-banner">
        <Buildings size={20} className="text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Azienda attiva</div>
          <div className="font-head font-bold truncate" data-testid="company-active-name">{current?.name || "—"}</div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((c) => (
          <div key={c.id} className="rounded-sm border border-border bg-card p-4 space-y-3"
            data-testid={`company-card-${c.id}`}>
            <div className="flex items-start gap-3">
              {c.logo_path ? (
                <img src={`${API}/companies/${c.id}/logo`} alt="" className="h-12 w-12 rounded-sm object-contain border border-border bg-white p-1" />
              ) : (
                <div className="h-12 w-12 rounded-sm bg-secondary flex items-center justify-center font-head font-bold">
                  {(c.name || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold truncate flex items-center gap-2">
                  {c.name}
                  {c.is_default && <Star size={14} weight="fill" className="text-amber-500" title="Predefinita" />}
                  {current?.id === c.id && <CheckCircle size={14} weight="fill" className="text-emerald-500" title="Attiva" />}
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.legal_name || "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {[c.address, c.zip_code, c.city, c.province && `(${c.province})`].filter(Boolean).join(" ") || "Indirizzo non impostato"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {[c.vat && `P.IVA ${c.vat}`, c.tax_code && `C.F. ${c.tax_code}`].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {current?.id !== c.id && (
                <Button size="sm" variant="outline" className="rounded-sm" data-testid={`company-select-${c.id}`}
                  onClick={() => act(() => api.post(`/companies/${c.id}/select`), `Ora stai lavorando su ${c.name}`)}>
                  Attiva
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" className="rounded-sm" data-testid={`company-edit-${c.id}`}
                    onClick={() => setForm({ ...c })}>Modifica</Button>
                  <label className="inline-flex">
                    <input type="file" accept="image/*" className="hidden" data-testid={`company-logo-${c.id}`}
                      onChange={(e) => e.target.files?.[0] && uploadLogo(c.id, e.target.files[0])} />
                    <span className="h-8 px-3 inline-flex items-center gap-1.5 rounded-sm border border-border text-sm font-medium cursor-pointer hover:bg-secondary">
                      <UploadSimple size={14} /> Logo
                    </span>
                  </label>
                  {!c.is_default && (
                    <Button size="sm" variant="outline" className="rounded-sm" data-testid={`company-default-${c.id}`}
                      onClick={() => act(() => api.post(`/companies/${c.id}/default`), "Azienda predefinita aggiornata")}>
                      Predefinita
                    </Button>
                  )}
                  {!c.is_default && (
                    <Button size="sm" variant="outline" className="rounded-sm text-rose-600" data-testid={`company-delete-${c.id}`}
                      onClick={() => act(() => api.delete(`/companies/${c.id}`), "Azienda eliminata")}>
                      <Trash size={14} />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="rounded-sm border border-border bg-card p-8 text-center text-muted-foreground" data-testid="companies-empty">
            Nessuna azienda configurata
          </div>
        )}
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="company-dialog">
            <DialogHeader><DialogTitle className="font-head">{form.id ? "Modifica azienda" : "Nuova azienda"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, l]) => (
                <div key={k} className={k === "name" || k === "legal_name" ? "sm:col-span-2" : ""}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
                  <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid={`cf-${k}`} />
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="company-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="company-save"
                onClick={() => {
                  const body = FIELDS.reduce((a, [k]) => ({ ...a, [k]: form[k] || "" }), {});
                  act(() => (form.id ? api.put(`/companies/${form.id}`, body) : api.post("/companies", body)), "Azienda salvata");
                }}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
